import {
  useCustomMutation,
  useInvalidate,
  useNotification,
  useTranslate,
} from '@refinedev/core';
import { useTable } from '@refinedev/antd';
import { Button, Card, Table, Tag, Tooltip, Typography } from 'antd';
import type { OAuthTokenOut } from '@/api/generated/schemas';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { orDash } from '@/components/common/show/shared';
import { formatDateTime } from '@/utils/format';
import { getSortOrder } from '@/utils/sorters';

const { Text } = Typography;

const RESOURCE = 'iam.oauth_tokens';

const RevokeTokenButton = ({
  tokenId,
  revoked,
}: {
  tokenId: number;
  revoked: boolean;
}) => {
  const t = useTranslate();
  const { open } = useNotification();
  const invalidate = useInvalidate();
  const { mutate, mutation } = useCustomMutation();

  return (
    <Button
      danger
      size="small"
      disabled={revoked}
      loading={mutation.isPending}
      onClick={() =>
        mutate(
          {
            url: `iam/oauth_tokens/${tokenId}/revoke`,
            method: 'patch',
            values: {},
          },
          {
            onSuccess: () => {
              open?.({
                type: 'success',
                message: t('notifications.success'),
                description: t('iam.oauth_tokens.actions.revoke_success'),
              });
              invalidate({ resource: RESOURCE, invalidates: ['list'] });
            },
          }
        )
      }
    >
      {t('iam.oauth_tokens.actions.revoke')}
    </Button>
  );
};

/** Unix seconds → formatted date. */
const formatIssuedAt = (value: number | null | undefined) =>
  value ? formatDateTime(new Date(value * 1000).toISOString()) : null;

export interface OAuthTokensTableProps {
  /** Which parent the tokens belong to. The matching column is hidden. */
  filter: { field: 'user_id' | 'client_id'; value: number | string };
  /** Translation key for the card title; the total count is appended. */
  titleKey: string;
}

/** Card + server-side paginated table of OAuth tokens scoped to a user or
 * client, with an inline revoke action. */
export const OAuthTokensTable = ({
  filter,
  titleKey,
}: OAuthTokensTableProps) => {
  const t = useTranslate();
  const { tableProps, sorters, result } = useTable<OAuthTokenOut>({
    resource: RESOURCE,
    filters: {
      permanent: [{ field: filter.field, operator: 'eq', value: filter.value }],
    },
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
    pagination: { pageSize: 10 },
    syncWithLocation: false,
  });

  return (
    <Card title={`${t(titleKey)} (${result?.total ?? 0})`}>
      <Table<OAuthTokenOut>
        {...tableProps}
        pagination={{ ...tableProps.pagination, simple: true }}
        rowKey="id"
        size="small"
      >
        <Table.Column<OAuthTokenOut>
          dataIndex="id"
          title={t('base.fields.id')}
          width={90}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
          render={(value: number) => (
            <ShowIconLink resource={RESOURCE} id={value}>
              {value}
            </ShowIconLink>
          )}
        />
        {filter.field !== 'user_id' && (
          <Table.Column<OAuthTokenOut>
            dataIndex="user_id"
            title={t('iam.oauth_tokens.fields.user_id')}
            render={(value: number) => (
              <ShowIconLink resource="iam.users" id={value}>
                {value}
              </ShowIconLink>
            )}
          />
        )}
        {filter.field !== 'client_id' && (
          <Table.Column<OAuthTokenOut>
            dataIndex="client_id"
            title={t('iam.oauth_tokens.fields.client_id')}
            ellipsis
          />
        )}
        <Table.Column<OAuthTokenOut>
          dataIndex="scope"
          title={t('iam.oauth_tokens.fields.scope')}
          render={(value: string | null) =>
            orDash(
              value ? (
                <Tooltip title={value}>
                  <Text
                    ellipsis
                    style={{ maxWidth: 160, display: 'inline-block' }}
                  >
                    {value}
                  </Text>
                </Tooltip>
              ) : null
            )
          }
        />
        <Table.Column<OAuthTokenOut>
          dataIndex="device_name"
          title={t('iam.oauth_tokens.fields.device_name')}
          render={(value: string | null) => orDash(value)}
        />
        <Table.Column<OAuthTokenOut>
          dataIndex="ip_address"
          title={t('iam.oauth_tokens.fields.ip_address')}
          render={(value: string | null) => orDash(value)}
        />
        <Table.Column<OAuthTokenOut>
          dataIndex="issued_at"
          title={t('iam.oauth_tokens.fields.issued_at')}
          render={(value: number) => orDash(formatIssuedAt(value))}
        />
        <Table.Column<OAuthTokenOut>
          dataIndex="revoked"
          title={t('iam.oauth_tokens.fields.revoked')}
          render={(value: boolean | undefined) => (
            <Tag color={value ? 'error' : 'success'}>
              {t(`iam.oauth_tokens.status.${value ? 'revoked' : 'active'}`)}
            </Tag>
          )}
        />
        <Table.Column<OAuthTokenOut>
          title={t('base.fields.actions')}
          width={120}
          render={(_: unknown, row) => (
            <RevokeTokenButton
              tokenId={row.id}
              revoked={row.revoked ?? false}
            />
          )}
        />
      </Table>
    </Card>
  );
};
